import { useEffect, useState } from "react";
import { Alert, Input, Modal, Select, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SECS, type AuditDetailDto, type QuestionDto } from "@audit-plan/shared";
import { api } from "../../api/client";
import { invalidateAudit } from "../../api/audits";
import { useFullModal } from "./mobileModal";
import { useIsMobile } from "../useIsMobile";
import { useNotify } from "./useNotify";

export function PhaseoutModal({ open, onClose, audit, questions }: { open: boolean; onClose: () => void; audit: AuditDetailDto; questions: QuestionDto[] }) {
  const { t, i18n } = useTranslation();
  const fm = useFullModal();
  const mob = useIsMobile();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const [reason, setReason] = useState("");
  const [questionId, setQuestionId] = useState<string | undefined>();
  // esito proposto dalle regole del rating (Amministrazione → Configurazione): precompila motivo e requisito
  useEffect(() => {
    if (open && audit.suggestedOutcome) {
      setReason((r) => r || audit.suggestedOutcome!.reason);
      setQuestionId((q) => q ?? audit.suggestedOutcome!.questionId);
    }
  }, [open, audit.suggestedOutcome]);
  const save = useMutation({
    mutationFn: () => api.post(`/audits/${audit.id}/phaseout`, { reason: reason.trim(), questionId: questionId ?? null }),
    onSuccess: () => {
      invalidateAudit(qc, audit.id);
      setReason("");
      setQuestionId(undefined);
      onClose();
    },
    onError: n.fail,
  });
  return (
    <Modal {...fm} open={open} onCancel={onClose} title={`${t("wf_phaseout")} · ${audit.code}`} okText={t("confirm")} cancelText={t("cancel")} okButtonProps={{ danger: true, disabled: !reason.trim() }} confirmLoading={save.isPending} onOk={() => save.mutate()} destroyOnHidden>
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message={en ? "The audit is closed with outcome phase-out: no CAP is opened and the supplier is flagged for phase-out." : "L'audit si chiude con esito phase-out: non si apre nessun CAP e il fornitore viene marcato in phase-out."}
      />
      <Typography.Text>{t("phaseoutReason")} *</Typography.Text>
      <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={en ? "e.g. overtime paid off the books" : "es. straordinari pagati fuori busta"} />
      <div style={{ marginTop: 12 }}>
        <Typography.Text>{t("question")}</Typography.Text>
        <Select
          style={{ width: "100%" }}
          allowClear
          showSearch
          optionFilterProp="label"
          value={questionId}
          onChange={setQuestionId}
          placeholder="—"
          options={questions.filter((q) => (SECS as readonly string[]).includes(q.section)).map((q) => ({ value: q.id, label: `${q.code} · ${(en ? q.en || q.it : q.it).slice(0, 70)}` }))}
        />
      </div>
    </Modal>
  );
}
